import { Icon, IconButton, Tooltip } from "@mui/material";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Proposal, ProposalProduct, ProposalStage } from "generated/graphql";
import SelectStageProduct from "./SelectStageProduct";
import AddProductToStage from "./AddProductToStage";
import React, { useCallback, useContext, useEffect, useState } from "react";
import ProposalContext from "./providers/ProposalContextProvider";
import useDeleteProposalProduct from "hooks/proposals/useDeleteProposalProduct";
import useUpdateProposalProductOverrides from "hooks/proposals/useUpdateProposalProductOverrides";
import useUpdateProposalProductDiscount from "hooks/proposals/useUpdateProposalProductDiscount";
import ReorderStageProducts from "./providers/ReorderStageProducts";
import useUpdateStageProductsOrdering from "hooks/proposals/useUpdateStageProductsOrdering";
import { DefinedButtons } from "components/Sidebar";
import { SidebarContext } from "components/Sidebar/sidebarContext";
import { CheckBoxOutlined } from "@mui/icons-material";
import MDButton from "components/MDButton";
import AddStageChecklistModal from "./AddStageChecklistModal";
import useAddProposalStageChecklistItems from "hooks/proposals/useAddProposalStageChecklistItems";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import ProductTableHeader from "./ProductTableHeader";
import ProductRow from "./ProductRow";
import MapModal from "modules/Mapmodal/MapModal";
import useModalState from "hooks/useModalState";
import useUpdateProposalStageAddressAndClearProducts from "hooks/proposals/useUpdateProposalStageAddressAndClearProducts";

export default function Stage({
  stage,
  expandedStage,
  onDeleteStage,
}: {
  stage: ProposalStage;
  expandedStage: ProposalStage["id"] | false;
  onDeleteStage?: (id: ProposalStage["id"]) => void;
}) {
  const { sendRequest, loading } = useUpdateStageProductsOrdering();
  const { inViewMode } = useContext(ProposalContext);
  const hasProducts = stage.proposalProducts.length > 0;
  const [expandedProduct, setExpandedProduct] = React.useState<string | false>(
    hasProducts ? stage.proposalProducts?.[0]?.id ?? false : false
  );
  const [sortedProducts, setSortedProducts] = useState<ProposalProduct[] | null>(
    stage.proposalProducts
  );

  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const {
    open: editAddressModalOpen,
    onClose: closeEditAddressModal,
    onOpen: openEditAddressModal,
  } = useModalState();
  const [addStageChecklistItems, { loading: saveChecklistLoading }] =
    useAddProposalStageChecklistItems();
  const [updateStageAddressAndClearProducts, updateStageAddressContext] =
    useUpdateProposalStageAddressAndClearProducts();

  const { clickedButton } = useContext(SidebarContext);

  useEffect(() => {
    if (clickedButton == DefinedButtons.ProposalCollapseProducts) {
      setExpandedProduct(false);
    }
  }, [clickedButton]);

  useEffect(() => {
    setSortedProducts(stage.proposalProducts);
  }, [stage.proposalProducts]);

  const [sortMode, setSortMode] = useState<boolean>(false);
  const [mode, setMode] = useState<"button" | "adding">("button");

  const [productToDelete, setProductToDelete] = useState<string | null>(null);

  const [deleteProposalProduct, { isSuccess, data }] = useDeleteProposalProduct();
  const [updateProductOverrides] = useUpdateProposalProductOverrides();
  const [updateProductDiscount] = useUpdateProposalProductDiscount();

  const handleProductToggle = useCallback(
    (productId: string, isExpanded: boolean) => {
      setExpandedProduct(isExpanded ? productId : false);
      if (isExpanded) {
        setTimeout(() => {
          document.getElementById(`panel-product-${productId}`)?.scrollIntoView({
            behavior: "smooth",
          });
        }, 200);
      }
    },
    [setExpandedProduct]
  );

  const onSuccess = useCallback(
    (updatedProposal: Proposal) => {
      // Here I have a proposal that just received a Product
      // Let's set the last product in the array as the expanded product
      const foundStage = updatedProposal?.stages?.find((stage) => stage.id === expandedStage);
      const lastProduct = foundStage?.proposalProducts?.slice(-1);
      setSortedProducts(foundStage.proposalProducts);
      if (lastProduct?.length > 0) {
        setExpandedProduct(lastProduct[0].id);
      }
    },
    [expandedStage]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const result = await deleteProposalProduct({
        variables: {
          id,
        },
      });
      const success = isSuccess(result.data);
      return {
        success,
        data,
      };
    },
    [deleteProposalProduct]
  );
  useEffect(() => {
    if (expandedStage !== stage.id && expandedProduct) {
      setExpandedProduct(false);
    }
  }, [expandedStage, stage.id]);

  const handleUpdateProductName = useCallback(
    async (productId: string, value: string) => {
      await updateProductOverrides({
        variables: {
          proposalProductId: productId,
          externalProductName: value.trim() === "" ? null : value,
        },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateProfit = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const profitOverride = trimmed === "" ? null : Math.round(parseFloat(trimmed) * 100);
      await updateProductOverrides({
        variables: { proposalProductId: productId, profitOverride },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateProfitMargin = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const profitPercentageOverride =
        trimmed === "" ? null : Math.max(1, Math.min(99, Math.round(parseFloat(trimmed))));
      await updateProductOverrides({
        variables: { proposalProductId: productId, profitPercentageOverride },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateTotalPrice = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const overallTotalOverride = trimmed === "" ? null : Math.round(parseFloat(trimmed) * 100);
      await updateProductOverrides({
        variables: { proposalProductId: productId, overallTotalOverride },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateUnitPrice = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const unitPriceOverride = trimmed === "" ? null : Math.round(parseFloat(trimmed) * 100);
      await updateProductOverrides({
        variables: { proposalProductId: productId, unitPriceOverride },
      });
    },
    [updateProductOverrides]
  );

  const handleUpdateDiscount = useCallback(
    async (productId: string, value: string) => {
      const trimmed = value.trim();
      const discount = trimmed === "" ? 0 : Math.round(parseFloat(trimmed) * 100);
      await updateProductDiscount({
        variables: { input: { id: productId, discount } },
      });
    },
    [updateProductDiscount]
  );

  const {
    open: deleteProductDialogOpen,
    setOpen: setDeleteProductDialogOpen,
    onAccept: onAcceptDeleteProduct,
    onCancel: onCancelDeleteProduct,
  } = useConfirmationDialog({
    acceptFn: async () => {
      if (productToDelete) {
        await handleDelete(productToDelete);
        setProductToDelete(null);
      }
      return true;
    },
    cancelFn: async () => {
      setProductToDelete(null);
      return true;
    },
  });

  const handleDeleteProductClick = useCallback(
    (productId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setProductToDelete(productId);
      setDeleteProductDialogOpen(true);
    },
    [setDeleteProductDialogOpen]
  );

  const {
    open: deleteStageDialogOpen,
    setOpen: setDeleteStageDialogOpen,
    onAccept: onAcceptDeleteStage,
    onCancel: onCancelDeleteStage,
  } = useConfirmationDialog({
    acceptFn: async () => {
      if (onDeleteStage) {
        onDeleteStage(stage.id);
      }
      return true;
    },
    cancelFn: async () => true,
  });

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const items = Array.from(sortedProducts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSortedProducts(items);
    sendRequest({
      id: stage.id,
      proposalProducts: {
        update: items.map((item, index) => ({ id: item.id, ordering: index + 1 })),
      },
    });
    setSortMode(false);
  };

  const handleSaveChecklist = (selectedItems: string[], notes: string) => {
    addStageChecklistItems({
      id: stage.id,
      checklistItems: {
        sync: selectedItems,
      },
      checklistNotes: notes,
    });
  };

  const handleMapSave = useCallback(
    async (data: any) => {
      const address = data.address;
      const result = await updateStageAddressAndClearProducts({
        variables: {
          input: {
            proposalStageId: stage.id,
            addressLine1: address.line1,
            addressLine2: address.line2 || undefined,
            addressCity: address.city || undefined,
            addressState: address.state || undefined,
            addressZip: address.zip || undefined,
            addressCountry: address.country || undefined,
            latitude: address.lat,
            longitude: address.lng,
            mapData: JSON.stringify(data.geojson),
            addressOverride: data.addressOverride || undefined,
          },
        },
      });

      const success = updateStageAddressContext.isSuccess(result.data);

      if (success) {
        closeEditAddressModal();
      }

      return success;
    },
    [stage.id, updateStageAddressAndClearProducts, updateStageAddressContext, closeEditAddressModal]
  );

  const stageAddress = [
    stage?.address?.line1,
    stage?.address?.city,
    stage?.address?.state,
    stage?.address?.zip,
    stage?.address?.country,
  ]
    .filter((x) => !!x)
    .join(", ");

  return (
    <>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDBox display="flex" alignItems="center" gap={2}>
          <MDTypography variant="h6" sx={{ fontSize: "1.25rem" }}>
            Job Address:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          </MDTypography>
          <MDTypography variant="body" sx={{ fontSize: "1.25rem" }}>
            {stage?.addressOverride ?? stageAddress}
          </MDTypography>
          {!inViewMode && stage?.address && (
            <MDButton
              variant="outlined"
              size="small"
              color="info"
              iconOnly
              type="button"
              onClick={openEditAddressModal}
            >
              <Icon>edit</Icon>
            </MDButton>
          )}
        </MDBox>
        {!inViewMode && (
          <Tooltip title={onDeleteStage ? "Delete Job" : "At least one job is required"}>
            <span>
              <IconButton
                size="medium"
                onClick={onDeleteStage ? () => setDeleteStageDialogOpen(true) : undefined}
                disabled={!onDeleteStage}
                sx={{
                  color: onDeleteStage ? "error.main" : "grey.400",
                  "&:hover": onDeleteStage
                    ? { backgroundColor: "error.light", color: "error.dark" }
                    : {},
                }}
              >
                <DeleteIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </MDBox>
      <MDBox
        py={1}
        display="flex"
        flexDirection="column"
        sx={{ background: !!sortMode ? "#fcfced" : "unset" }}
      >
        {!hasProducts && <SelectStageProduct stage={stage} onSuccess={onSuccess} />}
        {hasProducts && (
          <>
            <ProductTableHeader />

            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {sortedProducts.map((product, index) => (
                      <Draggable
                        key={product.id}
                        draggableId={product.id}
                        index={index}
                        isDragDisabled={!sortMode}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ProductRow
                              product={product}
                              isExpanded={expandedProduct === product.id}
                              inViewMode={inViewMode}
                              sortMode={sortMode}
                              onToggle={handleProductToggle}
                              onDelete={handleDeleteProductClick}
                              onUpdateName={handleUpdateProductName}
                              onUpdateProfit={handleUpdateProfit}
                              onUpdateMargin={handleUpdateProfitMargin}
                              onUpdateDiscount={handleUpdateDiscount}
                              onUpdateTotal={handleUpdateTotalPrice}
                              onUpdateUnit={handleUpdateUnitPrice}
                              stage={stage}
                              handleDelete={handleDelete}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
        {hasProducts && !inViewMode && (
          <MDBox
            pt={3}
            sx={{
              display: { xs: "block", md: mode === "button" ? "flex" : "block" },
              justifyContent: "space-between",
              textAlign: { xs: "center", md: "start" },
            }}
          >
            <AddProductToStage
              stage={stage}
              onSuccess={onSuccess}
              setExpandedProduct={setExpandedProduct}
              mode={mode}
              setMode={setMode}
            />
            <MDBox
              sx={{ display: { xs: "block", md: "flex" }, justifyContent: "flex-end", gap: 1 }}
            >
              {mode === "button" && sortedProducts.length > 1 && (
                <ReorderStageProducts
                  setSortMode={setSortMode}
                  disabled={sortMode || loading}
                  setExpandedProduct={setExpandedProduct}
                />
              )}
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<CheckBoxOutlined />}
                onClick={() => setChecklistModalOpen(true)}
              >
                Add Job Checklist
              </MDButton>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <AddStageChecklistModal
        open={checklistModalOpen}
        onClose={() => setChecklistModalOpen(false)}
        onSave={handleSaveChecklist}
        existingItems={stage.checklistItems?.map((item) => item.id) || []}
        existingNotes={stage.checklistNotes || ""}
        isSaving={saveChecklistLoading}
      />

      <ActionConfirmDialog
        open={deleteProductDialogOpen}
        setOpen={setDeleteProductDialogOpen}
        handleAccept={onAcceptDeleteProduct}
        handleCancel={onCancelDeleteProduct}
        title="Delete Product"
        content="Are you sure you want to delete this product from the job?"
        acceptText="Delete"
        cancelText="Cancel"
      />

      <ActionConfirmDialog
        open={deleteStageDialogOpen}
        setOpen={setDeleteStageDialogOpen}
        handleAccept={onAcceptDeleteStage}
        handleCancel={onCancelDeleteStage}
        title="Delete Job"
        content="Are you sure you want to delete this job from the proposal?"
        acceptText="Delete"
        cancelText="Cancel"
      />
      <MapModal
        open={editAddressModalOpen}
        onClose={closeEditAddressModal}
        guided
        onSave={handleMapSave}
      />
    </>
  );
}
